// Import the types for utility functions
import { Source, Destination, EntityType, ActionType } from '../../types/enums';

/**
 * Re-export existing enums from the main types module
 * These are the same enums used throughout the system for consistency
 */
export { Source, Destination, EntityType, ActionType } from '../../types/enums';

/**
 * Transformation path key with optional action suffix
 * Base: SOURCE.DESTINATION.ENTITY (e.g., TECHLOG.RUSADA.DEFECT)
 * With action: SOURCE.DESTINATION.ENTITY.ACTION (e.g., TECHLOG.RUSADA.DEFECT.DELETE)
 */
export type TransformationPathKey =
  | `${Source}.${Destination}.${EntityType}`
  | `${Source}.${Destination}.${EntityType}.${ActionType}`;

/**
 * Creates a transformation path key, optionally scoped to a specific action
 */
export function createTransformationPathKey(
  source: Source,
  destination: Destination,
  entity: EntityType,
  action?: ActionType,
): TransformationPathKey {
  const base = `${source}.${destination}.${entity}`;
  return (action ? `${base}.${action}` : base) as TransformationPathKey;
}

/**
 * Parses a transformation path key into its components
 */
export function parseTransformationPathKey(pathKey: TransformationPathKey): {
  source: Source;
  destination: Destination;
  entity: EntityType;
  action?: ActionType;
} {
  const parts = pathKey.split('.');
  const [sourceStr, destinationStr, entityStr, actionStr] = parts;

  const source = Object.values(Source).find((s) => s === sourceStr);
  const destination = Object.values(Destination).find(
    (d) => d === destinationStr,
  );
  const entity = Object.values(EntityType).find((e) => e === entityStr);
  const action = actionStr
    ? Object.values(ActionType).find((a) => a === actionStr)
    : undefined;

  if (!source || !destination || !entity) {
    throw new Error(`Invalid transformation path key: ${pathKey}`);
  }

  if (actionStr && !action) {
    throw new Error(`Invalid action in transformation path key: ${pathKey}`);
  }

  return { source, destination, entity, action };
}
